import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { FocusTarget, Options as FocusTrapOptions } from "focus-trap";
import { createFocusTrap } from "focus-trap";

import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";
import { useLatestRef } from "./useLatestRef";

export type FocusManagerOptions = {
  active?: boolean;
  focusTrapOptions?: FocusTrapOptions;
  initialFocus?: FocusTarget;
  onActivate?: () => void;
  onDeactivate?: () => void;
  returnFocusOnDeactivate?: boolean;
};

export type FocusManagerControls = {
  activate: () => void;
  deactivate: () => void;
  previouslyFocusedElement: HTMLElement | null;
  setContainer: (node: HTMLElement | null) => void;
};

export function useFocusManager({
  active = false,
  focusTrapOptions,
  initialFocus,
  onActivate,
  onDeactivate,
  returnFocusOnDeactivate = true,
}: FocusManagerOptions = {}): FocusManagerControls {
  const containerRef = useRef<HTMLElement | null>(null);
  const trapRef = useRef<ReturnType<typeof createFocusTrap> | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [containerNode, setContainerNode] = useState<HTMLElement | null>(null);
  const [previouslyFocusedElement, setPreviouslyFocusedElement] =
    useState<HTMLElement | null>(null);
  const outsideClickRef = useRef<PointerEvent | MouseEvent | TouchEvent | null>(
    null
  );

  const activateCallbackRef = useLatestRef(onActivate);
  const deactivateCallbackRef = useLatestRef(onDeactivate);
  const optionsRef = useLatestRef(focusTrapOptions);
  const returnFocusRef = useLatestRef(returnFocusOnDeactivate);

  const setContainer = useCallback((node: HTMLElement | null) => {
    containerRef.current = node;
    setContainerNode(node);
  }, []);

  const updatePreviouslyFocusedElement = useCallback(
    (element: HTMLElement | null) => {
      previouslyFocusedRef.current = element;
      setPreviouslyFocusedElement(element);
    },
    []
  );

  const ensureTrap = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return null;
    }

    if (trapRef.current) {
      trapRef.current.updateContainerElements?.([container]);
      return trapRef.current;
    }

    const trap = createFocusTrap(container, {
      ...optionsRef.current,
      initialFocus: initialFocus ?? optionsRef.current?.initialFocus,
      fallbackFocus: optionsRef.current?.fallbackFocus ?? (() => container),
      onActivate() {
        activateCallbackRef.current?.();
        optionsRef.current?.onActivate?.();
      },
      onDeactivate() {
        if (!outsideClickRef.current && returnFocusRef.current !== false) {
          previouslyFocusedRef.current?.focus?.();
        }

        outsideClickRef.current = null;
        deactivateCallbackRef.current?.();
        optionsRef.current?.onDeactivate?.();
      },
      clickOutsideDeactivates(event) {
        outsideClickRef.current = event;

        const option = optionsRef.current?.clickOutsideDeactivates ?? true;

        if (typeof option === "function") {
          if (event instanceof MouseEvent || event instanceof TouchEvent) {
            const result = option(event);
            if (result) {
              outsideClickRef.current = event;
            }

            return result;
          }

          return false;
        }

        if (option) {
          outsideClickRef.current = event;
        }

        return option;
      },
    });

    trapRef.current = trap;

    return trap;
  }, [
    activateCallbackRef,
    deactivateCallbackRef,
    initialFocus,
    optionsRef,
    returnFocusRef,
  ]);

  const activate = useCallback(() => {
    const trap = ensureTrap();

    if (!trap) {
      return;
    }

    trap.activate();
  }, [ensureTrap]);

  const deactivate = useCallback(() => {
    if (!trapRef.current) {
      return;
    }

    trapRef.current.deactivate({ returnFocus: false });
  }, []);

  useEffect(() => {
    if (!containerNode) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Node && !containerNode.contains(target)) {
        outsideClickRef.current = event;
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [containerNode]);

  useIsomorphicLayoutEffect(() => {
    if (!active) {
      deactivate();
      return;
    }

    const activeElement = document.activeElement;

    updatePreviouslyFocusedElement(
      activeElement instanceof HTMLElement ? activeElement : null
    );

    const animationFrame = requestAnimationFrame(() => {
      activate();
    });

    return () => {
      cancelAnimationFrame(animationFrame);
      deactivate();
    };
  }, [activate, deactivate, active, updatePreviouslyFocusedElement]);

  return useMemo(
    () => ({
      activate,
      deactivate,
      previouslyFocusedElement,
      setContainer,
    }),
    [activate, deactivate, previouslyFocusedElement, setContainer]
  );
}
